import { Box, Card, Divider, Typography, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import TitleContext from '../../TitleContext'
import { Page } from '../Elements/PageMargins'
import CCPlayer from './CCPlayer'
import { ContentAsset, PlaylistItem } from '../../swagger'
import EmptyPage from '../Elements/EmptyPage'
import { usePlaylistContext } from '../../hooks/usePlaylistContext'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import { FileType } from '../Elements/FileTypeResource'
import DynamicBreadcrumbs from '../Elements/DynamicBreadcrumbs'
import PlaylistItemControlsCard from '../Card/PlaylistItemControlsCard'
import { formatFilesize } from '../LearningCenter/LearningCenterContent'
import { usePlaylistLoop } from '../../hooks/usePlaylistLoop'
import { truncateText } from '../../utils/stringUtility'

/**
 * PlaylistContent Component
 *
 * Displays the contents of a playlist, allowing the user to play audio or video
 * items and navigate between them. It shows a media player, breadcrumbs,
 * and a card with item details and navigation controls.
 *
 * Hooks:
 * - Reads `playlistKey` and `playlistItemKey` from route params.
 * - Loads playlist data using `usePlaylistContext`.
 * - Updates page title using `TitleContext`.
 * - Uses `useTranslation` for i18n.
 *
 * UI:
 * - If playlist is not yet loaded or empty, shows `EmptyPage`.
 * - Displays breadcrumbs: Playlist > Playlist name > Item key.
 * - Renders the `CCPlayer` with the full playlist and the active item.
 * - Shows a `PlaylistItemControlsCard` with item details, size, description,
 *   and next/previous navigation.
 */

const PlaylistContent: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { useTitleEffect } = React.useContext(TitleContext)
  const navigate = useNavigate()

  const { playlistKey, playlistItemKey } = useParams<{
    playlistKey: string
    playlistItemKey: string
  }>()

  /** ******* PlaylistContext ****** */
  const { playlist, isLoading } = usePlaylistContext({
    playlistKey: +`${playlistKey ?? 0}`,
  })
  /** ******************* */

  const [playlistItemDetail, setPlaylistItemDetail] = useState<PlaylistItem>()
  const { isLoopEnabled, toggleLoop } = usePlaylistLoop()

  /**
   * When playlist or playlistItemKey changes, find the corresponding
   * PlaylistItem and update the `currentItem` state.
   */
  useEffect(() => {
    if (!playlist || !playlistItemKey) return

    const itemKey = Number(playlistItemKey)
    const selectedItem = playlist.playlistItems.find(
      (item) => item.playlistItemKey === itemKey
    )

    if (
      selectedItem?.asset?.isNdaRequired &&
      !selectedItem.asset.ndaAcknowledged
    ) {
      navigate(`/business/consent/${selectedItem.asset.assetKey}`)
      return
    }
    setPlaylistItemDetail(selectedItem)
  }, [playlist, playlistItemKey, navigate])

  const navigationDisabled = useMemo(() => {
    const playlistItems = playlist?.playlistItems ?? []
    if (!playlistItemDetail) {
      return { prev: false, next: false }
    }

    const currentIndex = playlistItems.findIndex(
      (item) => item.playlistItemKey === playlistItemDetail.playlistItemKey
    )

    if (currentIndex === -1) {
      return { prev: false, next: false }
    }

    const isFirstItem = currentIndex === 0
    const isLastItem = currentIndex === playlistItems.length - 1

    return {
      prev: !isLoopEnabled && isFirstItem,
      next: !isLoopEnabled && isLastItem,
    }
  }, [playlist?.playlistItems, playlistItemDetail, isLoopEnabled])

  const title = t('PlaylistContent.Title', 'Playlist Contents')
  useTitleEffect(title)
  /**
   * Compute the base path for this resource type (business/learning center).
   */
  const resourcePath = playlist?.playlist.playlistType
    .replace('Center', '')
    .toLowerCase()

  /**
   * Navigate to the next or previous item in the playlist.
   *
   * @param direction 'next' or 'prev'
   */
  const changeItems = (direction: 'next' | 'prev') => {
    const playlistItems = playlist?.playlistItems ?? []
    // Find the index of the currently selected item in the playlist
    const currentItem = playlistItems.findIndex(
      (item) => item.playlistItemKey === playlistItemDetail?.playlistItemKey
    )
    // If the current item is not found (findIndex returns -1), exit the function
    if (currentItem === -1) return
    // Determine the index of the next or previous item depending on the direction
    const goToIndex = direction === 'next' ? currentItem + 1 : currentItem - 1
    // If the new index is out of bounds (less than 0 or greater than the array length), exit
    if (goToIndex < 0 || goToIndex >= playlistItems.length) {
      if (direction === 'next' && goToIndex === playlistItems.length) {
        return navigate(
          `/${resourcePath}/playlist/content/${playlist?.playlist.playlistKey}/${playlistItems[0].playlistItemKey}`
        )
      }

      if (direction === 'prev' && currentItem === 0) {
        return navigate(
          `/${resourcePath}/playlist/content/${
            playlist?.playlist.playlistKey
          }/${playlistItems[playlistItems.length - 1].playlistItemKey}`
        )
      }
      return
    }
    // Navigate to the route of the new item using its playlistItemKey
    return navigate(
      `/${resourcePath}/playlist/content/${playlist?.playlist.playlistKey}/${playlistItems[goToIndex].playlistItemKey}`
    )
  }

  /**
   * If no playlist is loaded yet, show EmptyPage with a loader.
   */
  if (!playlist) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'PlaylistContent.Message.NoPlaylistData',
              'No Playlist information available.'
            )}
          </Typography>
        }
        isLoading={isLoading}
      />
    )
  }
  /**
   * Determine whether the current item is an audio file.
   */
  const isAudio = playlistItemDetail?.asset?.filetype === FileType.Audio

  const playlistLabel = t('PlaylistContent.Breadcrumb.Playlist', 'Playlist')
  const playlistName = t(
    'PlaylistContent.Breadcrumb.PlaylistName',
    '{{name}}',
    {
      name: playlist.playlist.name ?? '',
    }
  )
  const assetNameCurrent = t(
    'PlaylistContent.Breadcrumb.PlaylistItemKey',
    '{{playlistItemKey}}',
    {
      playlistItemKey: playlistItemDetail?.asset?.title,
    }
  )

  return (
    <Page>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            label: playlistLabel,
            onClick: () => navigate(`/${resourcePath}/playlists`),
          },
          {
            label: truncateText(playlistName),
            onClick: () =>
              navigate(
                `/${resourcePath}/playlist-details/${playlist.playlist.playlistKey}`
              ),
          },
          {
            label: truncateText(assetNameCurrent),
          },
        ]}
      />
      <Card>
        <Box
          position="relative"
          height={{ xs: isAudio ? '32vh' : '100%', md: '67vh' }}
          p={theme.spacing(2)}
        >
          <CCPlayer
            playlist={
              playlist?.playlistItems.map((item) => ({
                ...item.asset,
                playlistKey: playlist?.playlist.playlistKey,
                playlistItemKey: item.playlistItemKey,
              })) as (ContentAsset & {
                playlistKey: number
                playlistItemKey: number
              })[]
            }
            loop={isLoopEnabled}
            PlaylistItemKey={+(playlistItemKey ?? '0')}
            playlistType={playlist.playlist.playlistType}
          />
        </Box>

        <Divider variant="middle" />

        <Box
          sx={{
            marginY: theme.spacing(2),
          }}
        >
          <Box sx={{ width: '100%', paddingX: theme.spacing(2) }}>
            <PlaylistItemControlsCard
              icon={
                !isAudio ? (
                  <PlayArrowIcon sx={{ width: '100%', height: '100%' }} />
                ) : (
                  <MusicNoteIcon sx={{ width: '100%', height: '100%' }} />
                )
              }
              title={playlistItemDetail?.asset?.title ?? ''}
              iconBackground={'dark'}
              subText={`Size: ${formatFilesize(
                playlistItemDetail?.asset?.filesize ?? 0
              )}`}
              actions={{
                nextItem: () => changeItems('next'),
                previousItem: () => changeItems('prev'),
                handleLoop: toggleLoop,
              }}
              description={playlistItemDetail?.asset?.description ?? ''}
              isLoop={isLoopEnabled}
              disablePrev={navigationDisabled.prev}
              disableNext={navigationDisabled.next}
            />
          </Box>
        </Box>
      </Card>
    </Page>
  )
}

export default PlaylistContent
